import { FocusContext } from '@noriginmedia/norigin-spatial-navigation'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { triggerSentryError } from '../../../../shared/helpers'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import { signoutUser } from '../../actions/userActions'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS'
import Spinner from '../../components/Spinner'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import googleAnalytics4 from '../../lib/googleAnalytics'
import tts from '../../lib/TTS'

const SignoutScreenTS = () => {
  const [error, setError] = useState(false)
  const [renderSpinner, setRenderSpinner] = useState(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)

  const { focusKey, ref, focusSelf } = useHybridFocusable({
    focusKey: 'SIGNOUT_SCREEN',
  })

  useKeyDown(KeyTypeEnum.Back, () => {
    handleCancel()
  })

  // Workaround to allow new focus components to work with spatial navigation and old components work with focusable HoC
  // TODO: remove this when whole app is migrated to react spatial navigation
  useEffect(() => {
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [])

  useEffect(() => {
    tts.stopRead()

    if (!user.email) return

    tts.pushTextToRead(
      `Currently signed in as ${user.email}. In order to switch your account, you must sign out of the PBS app and sign in again.`
    )

    googleAnalytics4.sendPageViewEvent('Sign-out')

    const timer = setTimeout(() => {
      focusSelf()
    }, 100)

    return () => {
      tts.stopRead()
      clearTimeout(timer)
    }
  }, [user.email, focusSelf])

  const handleSignOut = async () => {
    try {
      setRenderSpinner(true)
      await signoutUser()
      dispatch({ type: 'SIGN_OUT' })
      navigate('/activate')
    } catch (err: any) {
      setError(true)
      setRenderSpinner(false)
      triggerSentryError('signOut', 'SignoutScreenTS.tsx', {
        errorMessage: err.message ?? 'Undefined error message (default)',
        errorLocation: 'SignoutScreenTS - signOut',
      })
    }
  }

  const handleCancel = () => {
    navigate(-1)
  }

  const buttonSpeaker = (text: string) => {
    tts.stopRead()
    tts.addTextToRead(text)
  }

  const signOutLabel = error ? 'Try Again' : 'Sign Out'

  if (error) {
    tts.addTextToRead(
      'Unable to deactivate at this time. Please try again later.'
    )
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="signout-screen-container">
        {renderSpinner ? (
          <Spinner />
        ) : (
          <>
            <div className="text-container">
              <h1 className="title">Currently signed in as {user.email}</h1>
              <p className="text">
                In order to switch your account, you must sign out of the PBS
                app and sign in again.
              </p>
            </div>

            <div className="signout-button-container">
              <div className="button-container">
                <FocusableButtonTS
                  focusKey="signout-cancel-button"
                  onEnterPress={handleCancel}
                  onFocus={() => buttonSpeaker('Focused on Cancel')}
                  className="focusable-button"
                  stealFocus
                >
                  Cancel
                </FocusableButtonTS>
              </div>

              <div className="button-container">
                <FocusableButtonTS
                  focusKey="signout-confirm-button"
                  onEnterPress={handleSignOut}
                  onFocus={() => buttonSpeaker(`Focused on ${signOutLabel}`)}
                  className="focusable-button"
                >
                  {signOutLabel}
                </FocusableButtonTS>
              </div>
            </div>

            <p className={`error-message ${error ? 'errored' : ''}`}>
              Unable to deactivate at this time. Please try again later.
            </p>
          </>
        )}
      </div>
    </FocusContext.Provider>
  )
}

export default SignoutScreenTS
